import {
  userCoursePerformanceId,
  type CriticalActionEvent,
  type CriticalSubActionEvent,
  type PlayerHintEvent,
  type PlayerRun,
  type SceneModule,
  type ScoringRunResult,
  type Stage,
  type SupplySelectionEvent
} from '@webapp2/shared/model'
import { toRunResult, toStages } from '@webapp2/shared/run-result'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { type DebriefRouteLocation } from '../router'
import { state } from '../state'
import { ensureModuleBuilds } from './build-service'

export async function handleRunEnd(runLog: RunLog) {
  try {
    await processRunLog(runLog)
  } catch (error) {
    console.error(`Error processing run log ${runLog.id}:`, error)
  }
}

function parseRunLog(runLog: RunLog): PlayerRun {
  const criticalActions: CriticalActionEvent[] = []
  const hints: PlayerHintEvent[] = []
  const supplySelectionsByCriticalActionId = new Map<string, SupplySelectionEvent>()
  const criticalSubActionsById = new Map<string, CriticalSubActionEvent>()

  let courseId: string | undefined
  let moduleBuildId: string | undefined
  let multiplayerSessionId: string | undefined
  let techniqueId = ''
  let isTest = false
  let completed = false
  let exitModule = false
  let time = 0

  for (const row of runLog.rows) {
    switch (row.type) {
      case 'RunLog':
        row.json.systemInfo.commandLineArgs.forEach((value, index, args) => {
          if (value === '-ms') {
            multiplayerSessionId = args[index + 1]
          }
          if (value === '-cid') {
            courseId = args[index + 1]!
          }
          if (value === '-b') {
            moduleBuildId = args[index + 1]!
          }
        })
        isTest = row.json.isTestMode
        techniqueId = row.json.techniqueId.toLocaleLowerCase()
        break
      case 'EventLog':
        switch (row.json.type) {
          case 'Hint':
            hints.push({ id: row.json.data.id, criticalAction: row.json.data.criticalAction })
            break
          case 'CriticalActionCompleted':
            criticalActions.push({ id: row.json.data.id, passed: row.json.data.passed })
            break
          case 'CriticalSubActionCompleted':
            criticalSubActionsById.set(`${row.json.data.parent}:${row.json.data.id}`, row.json.data)
            break
          case 'SupplySelectionVerified':
            supplySelectionsByCriticalActionId.set(row.json.data.id, row.json.data)
            break
          case 'RunComplete':
            completed = true
            time = row.json.time
            break
          case 'ExitModule':
            exitModule = true
            if (!completed) {
              time = row.json.time
            }
            break
          case 'TechniqueManager.OnApplicationQuit()':
            if (!completed) {
              time = row.json.time
            }
            break
          case 'Restart':
            criticalActions.length = 0
            hints.length = 0
            supplySelectionsByCriticalActionId.clear()
            criticalSubActionsById.clear()
            completed = false
            exitModule = false
            time = 0
            break
        }
        break
    }
  }

  return {
    id: runLog.id,
    criticalActions,
    hints,
    supplySelections: Array.from(supplySelectionsByCriticalActionId.values()),
    criticalSubActions: Array.from(criticalSubActionsById.values()),
    score: 0,
    baseScore: 0,
    passed: false,
    isTest,
    completed,
    time,
    hintsUsed: hints.length,
    failedContaminationCriticalActionCount: 0,
    courseId,
    moduleBuildId,
    multiplayerSessionId,
    exitModule,
    techniqueId
  }
}

const contaminationCriticalActionIds = new Set(['MaintainSterility'])

function calculateScores(playerRun: PlayerRun, stages: Stage[]) {
  const criticalActionsById = new Map(playerRun.criticalActions.map(x => [x.id, x]))

  let totalCriticalActions = 0
  let passedCriticalActions = 0

  for (const stage of stages) {
    for (const criticalAction of stage.criticalActions) {
      totalCriticalActions++
      const completed = criticalActionsById.get(criticalAction.id)
      if (completed?.passed) {
        passedCriticalActions++
      }
    }
  }

  const rawBaseScore = totalCriticalActions > 0 ? (passedCriticalActions / totalCriticalActions) * 100 : 0
  playerRun.baseScore = Math.round(rawBaseScore)

  playerRun.failedContaminationCriticalActionCount = playerRun.criticalActions.filter(
    x => !x.passed && contaminationCriticalActionIds.has(x.id)
  ).length

  const hintReduction = playerRun.isTest ? playerRun.hintsUsed * 10 : 0
  const contaminationReduction = playerRun.failedContaminationCriticalActionCount * 30

  const rawScore = Math.max(0, rawBaseScore - hintReduction - contaminationReduction)
  playerRun.score = Math.round(rawScore)
  playerRun.passed = playerRun.isTest && playerRun.score >= 80
}

async function processRunLog(runLog: RunLog) {
  const playerRun = parseRunLog(runLog)

  if (!playerRun.techniqueId) {
    throw new Error(`Run processing requires technique for run: ${playerRun.id}`)
  }

  const stages = await getStages(playerRun)
  if (!stages) {
    throw new Error(`Stages missing for run ${playerRun.id}`)
  }

  // Calculate scores
  calculateScores(playerRun, stages)

  const runResult = toRunResult(playerRun, stages)
  state.runResults[runResult.id] = runResult
}

export async function handleRunResult(
  runResult: ScoringRunResult,
  runId: string,
  launchData: { courseId?: string; isTestMode: boolean; build: { id?: string; techniqueId: string } },
  sceneModules: SceneModule[]
) {
  const criticalActions: CriticalActionEvent[] = (runResult.criticalActionResults ?? []).map(x => ({
    id: x.id,
    passed: x.result === 'Passed'
  }))
  const completed = criticalActions.length > 0 && runResult.criticalActionResults.every(x => x.result !== 'NotAttempted')

  const playerRun: PlayerRun = {
    id: runId,
    techniqueId: launchData.build.techniqueId.toLowerCase(),
    criticalActions,
    hints: [],
    supplySelections: [],
    criticalSubActions: [],
    score: Math.round(runResult.score),
    baseScore: Math.round(runResult.baseScore),
    passed: runResult.passed,
    isTest: launchData.isTestMode,
    completed,
    time: runResult.time,
    hintsUsed: runResult.hintsUsed,
    failedContaminationCriticalActionCount: 0,
    courseId: launchData.courseId,
    moduleBuildId: launchData.build.id
  }

  const sceneModule = sceneModules.find(x => x.techniqueId.toLowerCase() === playerRun.techniqueId)
  const stages = toStages(sceneModule?.flow, sceneModule?.rubric) ?? (await getStages(playerRun))
  if (!stages) {
    console.warn(`No stages found for run result, using empty stages`)
    state.runResults[runId] = toRunResult(playerRun, [])
    return
  }
  state.runResults[runId] = toRunResult(playerRun, stages)
}

async function getStages(run: { moduleBuildId?: string; techniqueId: string }): Promise<Stage[] | undefined> {
  if (run.moduleBuildId) {
    await ensureModuleBuilds([run.moduleBuildId])
    return state.moduleBuilds[run.moduleBuildId]?.stages
  }
  const playerBuild = state.vuplex?.playerBuildId ? state.playerBuilds[state.vuplex.playerBuildId] : undefined
  return playerBuild?.embeddedModules?.find(m => m.techniqueId.toLowerCase() === run.techniqueId)?.stages
}

export function generateMotivationalContent(runId: string, isTestMode: boolean, courseId: string) {
  const course = state.courses[courseId]!
  const expectedStages = state.runResults[runId]!.stageResults

  const completedStages = expectedStages.filter(stage =>
    stage.criticalActionResults.every(x => x.passed && !x.hints.length)
  )
  const failedStages = expectedStages.filter(stage =>
    stage.criticalActionResults.some(x => !x.passed || x.hints.length)
  )

  if (completedStages.length === expectedStages.length) {
    return {
      summaryType: isTestMode ? 'test-all' : 'practice-all',
      title: '\uD83C\uDF89 Outstanding Performance!',
      message: isTestMode
        ? `Excellent! You've completed the ${course.name} training. You can practice again to explore the procedure and your current completion score will be kept.`
        : "Great work! Try in test mode to apply everything you've learned so far!",
      bgColor: 'bg-grey-teal-100',
      borderColor: 'border-green-light'
    }
  }

  if (completedStages.length > 0) {
    const greatWork = `Great work! You did excellent on ${completedStages[0]!.title}. Try to improve on ${failedStages[0]!.title}.`
    return {
      summaryType: isTestMode ? 'test-some' : 'practice-some',
      title: '\uD83D\uDE80 Great Progress!',
      message: isTestMode
        ? `${greatWork} Retry test mode or try in practice mode for additional guidance.`
        : `${greatWork} If you think you are ready, start test mode to apply everything you've learned so far!`,
      bgColor: 'bg-light-steel-blue-100',
      borderColor: 'border-blue2-light'
    }
  }

  return {
    summaryType: isTestMode ? 'test-none' : 'practice-none',
    title: '\uD83D\uDCAA Keep Going!',
    message: isTestMode
      ? 'Good job! Try again in practice mode for extra guidance on proper technique.'
      : 'Nicely Done! Now that you have explored the scenario, try to successfully complete all stages.',
    bgColor: 'bg-saffron-mango-100',
    borderColor: 'border-yellow-light'
  }
}

export function useRunRoute() {
  const route = useRoute() as DebriefRouteLocation
  const runResult = computed(() => state.runResults[route.params.runId]!)
  const courseId = runResult.value.courseId!
  const userCoursePerformance = computed(
    () => state.userCoursePerformances[userCoursePerformanceId(state.organizationId!, courseId, state.user!.id)]
  )
  return { route, runResult, userCoursePerformance }
}

export type Row<T> = {
  id: string
  type: string
  json: T
}

export type RunLogRow = Row<RunLogRowData> & { type: 'RunLog' }

export type RunLogRowData = {
  techniqueId: string
  systemInfo: SystemInfo
  isTestMode: boolean
}

export type SystemInfo = {
  commandLineArgs: string[]
}

export type EventLogRow<T, U extends string> = Row<EventLog<T> & { type: U }> & { type: 'EventLog' }

export type EventLog<T> = {
  id: string
  type: string
  chapterId: string
  time: number
  data: T
}

export type RunCompleteRow = EventLogRow<object, 'RunComplete'>
export type ExitModuleRow = EventLogRow<object, 'ExitModule'>
export type ApplicationQuitRow = EventLogRow<object, 'TechniqueManager.OnApplicationQuit()'>
export type RestartRow = EventLogRow<object, 'Restart'>

export type CriticalActionRow = EventLogRow<CriticalActionEvent, 'CriticalActionCompleted'>
export type CriticalSubActionRow = EventLogRow<CriticalSubActionEvent, 'CriticalSubActionCompleted'>

export type HintRow = EventLogRow<PlayerHintEvent & { criticalAction: string }, 'Hint'>

export type SupplySelectionVerifiedRow = EventLogRow<SupplySelectionEvent, 'SupplySelectionVerified'>

export type RunLog = {
  id: string
  rows: (
    | RunLogRow
    | HintRow
    | CriticalActionRow
    | CriticalSubActionRow
    | RunCompleteRow
    | ApplicationQuitRow
    | ExitModuleRow
    | RestartRow
    | SupplySelectionVerifiedRow
  )[]
}
